import { styled, Card, CardContent } from '@mui/material';
import { useMediaQuery } from 'react-responsive';
import clsx from 'clsx';
import {
  LayoutProps,
  SkipNavigationButton,
  LayoutClasses,
  Inspector,
  Sidebar as DefaultSidebar,
  Button,
  useLogout,
  useTranslate,
} from 'react-admin';
import { useHref } from 'react-router-dom';
import Grid2 from '@mui/material/Unstable_Grid2';
import ReactMarkdown from 'react-markdown';
import { AppLocationContext } from '@react-admin/ra-navigation';
import React, { useMemo, useState } from 'react';
import { useRedirect } from 'ra-core';
import { CustomMenu } from './Menu';
import authProvider from '../../utils/authProvider';
import { MyAppBar } from './MyAppBar';
import { useAutoUnselector } from '../../utils/selectionResetter';
import { XGAC_OTHER_DOMAIN } from '../../config';

const Grid = Grid2;

const PREFIX = 'RaLayout';
const StyledLayout = styled('div', {
  name: PREFIX,
  overridesResolver: (props, styles) => styles.root,
})(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  zIndex: 1,
  minHeight: '100vh',
  backgroundColor: theme.palette.background.default,
  position: 'relative',
  minWidth: 'fit-content',
  width: '100%',
  color: theme.palette.getContrastText(theme.palette.background.default),

  [`& .${LayoutClasses.appFrame}`]: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    marginTop: theme.spacing(6),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(7),
    },
  },
  [`& .${LayoutClasses.contentWithSidebar}`]: {
    display: 'flex',
    flexGrow: 1,
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  [`& .${LayoutClasses.content}`]: {
    backgroundColor: theme.palette.background.default,
    zIndex: 2,
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    flexBasis: 0,
    padding: 0,
    [theme.breakpoints.up('xs')]: {
      paddingRight: theme.spacing(2),
      paddingLeft: theme.spacing(1),
    },
  },
}));

export const XGACLayout = (props: LayoutProps) => {

  useAutoUnselector();
  // styling
  import('./scss/App.scss');

  const {
    appBar: AppBar = MyAppBar,
    children,
    className,
    error: errorComponent,
    menu: Menu = CustomMenu,
    sidebar: Sidebar = DefaultSidebar,
    ...rest
  } = props;

  const translate = useTranslate();
  const logout = useLogout();
  const hoverToView = translate('general.text.hover_to_view');
  const roles = authProvider.getRoles();
  const redirect = useRedirect();

  const [mobileCheckSkipped, setMobileCheckSkipped] = useState(false);

  const isMobile = useMediaQuery({ query: '(max-width: 760px)' });

  // determine fullscreen map
  const fullscreenPages = ['/live-map', '/zone-map', '/overlays', '/kiosks', '/kiosks-beta'];

  const allowedPagesPerRole = {
    customer_admin: ['/app-users', '/users/without-app', '/asset-groups', '/alarms', '/templates', '/overlays', '/report-triggers', '/zone-map', '/reports'],
    map_viewer: ['/live-map'],
    flic_hub_tester: ['/flic-hubs', '/flic-buttons'],
    sales_global: ['/customer-access-requests'],
    support_global: ['/customer-access-requests'],
    developer_admin: ['/client-config-manager', '/client-config-groups'],
  };

  const href = useHref('?');
  const isFullscreen = fullscreenPages.includes(href);

  const allAllowedPages = useMemo(() => {

    const allowedPages: string[] = [];
    if (!roles) {

      return allowedPages;

    }
    for (const role of roles) {

      const currentAllowedPages = allowedPagesPerRole[role as keyof typeof allowedPagesPerRole] || [];
      allowedPages.push(...currentAllowedPages);

    }
    return allowedPages;

  }, [roles]); // eslint-disable-line react-hooks/exhaustive-deps

  const bypassAllowedPagesCheck = roles.some((role: string) => (
    ['admin', 'sales_admin', 'support_admin'].includes(role)
  ));

  if (!bypassAllowedPagesCheck && href !== '/') {

    if (!allAllowedPages.some((page) => href.startsWith(page))) {

      redirect('/');

    }

  }

  if (isMobile && !mobileCheckSkipped) {

    return (
      <div className="mobile-div">
        <div>
          <img src='/assets/img/logo.svg' alt="X-Guard" width="90%" style={{
            marginLeft: 'auto', marginRight: 'auto', paddingTop: '20px', paddingBottom: '20px', display: 'block',
          }}/>
        </div>
        <Grid container justifyContent="center" alignItems="center">
          <Grid xs={11}>
            <Card>
              <CardContent>
                <span>
                  {translate('general.text.mobileWarning')}
                </span>
                <Button onClick={() => setMobileCheckSkipped(true)} variant="contained" sx={{
                  marginRight: 0, marginLeft: 'auto', marginTop: '10px', display: 'block',
                }}>
                  <>{translate('general.text.proceed')}</>
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

      </div>
    );

  }

  if (roles.includes('app_user')) {

    return (
      <div className="mobile-div">
        <div>
          <img src='/assets/img/logo.svg' alt="X-Guard" width="50%" style={{
            marginLeft: 'auto', marginRight: 'auto', paddingTop: '20px', paddingBottom: '20px', display: 'block',
          }}/>
        </div>
        <Grid container justifyContent="center" alignItems="center">
          <Grid xs={11}>
            <Card>
              <CardContent>
                <ReactMarkdown>
                  {translate('general.text.appUserWarning')}
                </ReactMarkdown>
                <div style={{ width: '100%', display: 'flex', justifyContent: 'end' }}>
                  {((roles.includes('bhvk_admin') || roles.includes('bhvk_trial')) && !authProvider.isAdmin()) && (
                    <Button onClick={() => {

                      window.location.href = XGAC_OTHER_DOMAIN;

                    }} variant="contained" sx={{
                      marginRight: '10px',
                    }}>
                      <>{translate('general.text.go_to_bhvk')}</>
                    </Button>
                  )}
                  <Button onClick={logout} variant="contained">
                    <>{translate('ra.auth.logout')}</>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

      </div>
    );

  }

  return (
    <AppLocationContext>
      <style>{`.privacyBlur::before { content: "${hoverToView}"; }`}</style>
      <StyledLayout className={clsx('layout', className)} {...rest}>
        <SkipNavigationButton />
        <div className={LayoutClasses.appFrame}>
          <AppBar/>
          <main className={LayoutClasses.contentWithSidebar}>
            <Sidebar>
              <Menu/>
            </Sidebar>
            <div id="main-content" className={`${LayoutClasses.content}${isFullscreen ? ' main-content-fullscreen' : ''}`}>
              {children}
            </div>
          </main>
          <Inspector />
        </div>
      </StyledLayout>
    </AppLocationContext>
  );

};
